import { useCallback, useDebugValue, useEffect } from 'react';

import { isKeyPressed } from './keyboard';
import type { KEY_NAME } from './keyName';

export const useKeyboardEventCallback = (
  element: Node | Window | null,
  keyCode: KEY_NAME | KEY_NAME[],
  callback: (event: KeyboardEvent) => void,
  { condition = true, preventDefault = false, stopPropagation = false } = {},
): void => {
  useDebugValue(`key: ${keyCode.toString()}`);

  const handleEvent = useCallback(
    (event: Event) => {
      if (!isKeyPressed(event as KeyboardEvent, keyCode)) {
        return;
      }

      if (preventDefault) {
        event.preventDefault();
      }

      if (stopPropagation) {
        event.stopPropagation();
      }

      callback(event as KeyboardEvent);
    },
    [keyCode, callback, preventDefault, stopPropagation],
  );

  useEffect(() => {
    if (element && condition) {
      element.addEventListener('keydown', handleEvent);
    }

    return () => {
      if (element) {
        element.removeEventListener('keydown', handleEvent);
      }
    };
  }, [condition, element, handleEvent]);
};
